import { PayloadAction } from '@reduxjs/toolkit';
import { quoteBuilderFieldValidators } from '@ui/features/quote-builder/state/fields';
import { WritableDraft } from 'immer';
import { z } from 'zod/v4';

import { QuoteBuilderStateType } from '../../types/state';
import { QuoteEditableSelectFields } from '../../types/state/quote';
import { buildElementId } from '../utilities/element-id';
import { buildIssueId } from '../utilities/issue-id';
import { deleteIssue, upsertIssue } from '../utilities/issue-managers';

/**
 * Updates an editable select field in the QuoteBuilderState and performs validation if a validator is provided.
 *
 * Note that only the first validation error will be displayed in the UI if multiple errors occur.
 */
export function editableSelectFieldChanged(
  state: WritableDraft<QuoteBuilderStateType>,
  action: PayloadAction<{
    field: QuoteEditableSelectFields;
    value: string;
  }>,
) {
  const { field, value } = action.payload;
  const validator = quoteBuilderFieldValidators[field];
  const elementId = buildElementId({
    uuid: state.uuid,
    field,
  });

  state.rawFieldValues[elementId] = value;

  let newValue: string | null = value;

  const invalidIssueId = buildIssueId({
    area: 'quote',
    field,
    type: 'invalid',
  });

  if (validator) {
    const parsedValue = validator.safeParse(value);

    if (!parsedValue.success) {
      const error = z.flattenError(parsedValue.error).formErrors[0];

      upsertIssue(state, invalidIssueId, {
        action: {
          type: 'modify',
          elementId,
        },
        area: 'quote',
        description: error,
        severity: 'error',
        title: 'Invalid Field',
        type: 'invalid',
      });

      return;
    }

    newValue = parsedValue.data;
  }

  deleteIssue(state, invalidIssueId);

  state[field] = newValue;
}
